import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  MIDDLE_WARE_BASE_URL,
  CLASSIFY_IDENTIFIER_API,
} from "../../../../utils/CONSTANTS";
import axios from "../../../../utils/axios";

// Unified email-first routing. Given an identifier (email), the V2 backend
// (NonSSOLogin/classify) returns which auth path the user belongs on. Under the
// D-strict classifier the backend routes by email domain only and returns just:
//   "sso"      -> corporate domain (AppSettings:SSODomains) -> PingOne
//   "password" -> any other valid email -> non-SSO password form (register is
//                 reached from there via "Create an account")
// "register" is retained in the type/handlers as a defensive no-op in case the
// backend contract changes. The classify call is read-only and generates no OTP.

type ClassifyRoute = "sso" | "password" | "register" | null;

const initialState = {
  route: null as ClassifyRoute,
  classifyState: "" as "" | "pending" | "fulfilled" | "failed",
  classifyError: "",
};

const RETRIES = 2; // total attempts = RETRIES + 1
const BASE_DELAY_MS = 300; // exponential backoff base
const TIMEOUT_MS = 8000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Aborts the in-flight XHR when the timeout fires so timed-out attempts don't
// linger as pending requests (the retry loop would otherwise stack them up).
// A CanceledError has no response.status, so the loop treats it as a transient
// failure and retries — same intent as the previous timeout behavior.
const requestWithTimeout = (url: string, ms: number): Promise<any> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return axios
    .getRequest(url, {}, { signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

// The classify envelope matches other V2 responses: payload.data.response is a
// JSON string carrying { route }. Parse defensively (string or already-object).
const parseRoute = (payload: any): ClassifyRoute => {
  try {
    const raw = payload?.data?.response;
    if (!raw) return null;
    if (typeof raw === "object") return (raw.route ?? null) as ClassifyRoute;
    let json = JSON.stringify(raw)
      .replace(/\\"/gi, '"')
      .replace('"{', "{")
      .replace('}"', "}");
    return (JSON.parse(json)?.route ?? null) as ClassifyRoute;
  } catch {
    return null;
  }
};

export const classifyIdentifier = createAsyncThunk(
  "get/classifyIdentifier",
  async (identifier: string, { rejectWithValue }) => {
    const url =
      MIDDLE_WARE_BASE_URL() +
      CLASSIFY_IDENTIFIER_API() +
      "?identifier=" +
      encodeURIComponent(identifier);

    let lastError: any = null;
    for (let attempt = 0; attempt <= RETRIES; attempt++) {
      try {
        return await requestWithTimeout(url, TIMEOUT_MS);
      } catch (error: any) {
        lastError = error;
        // 400 = bad identifier, 429 = rate-limited — both definitive; do not
        // retry (retrying a 429 only adds load and delays the user's error).
        // Retry only on network / 5xx / timeout (transient) failures.
        const status = error?.response?.status;
        if (status === 400 || status === 429) break;
        if (attempt < RETRIES) {
          await sleep(BASE_DELAY_MS * Math.pow(2, attempt));
        }
      }
    }
    return rejectWithValue(lastError?.message || "classify_failed");
  }
);

export const classifyIdentifierSlice = createSlice({
  name: "classifyIdentifier",
  initialState,
  reducers: {
    // Clears routing state, e.g. when the user taps "Change" on the email chip.
    resetClassify: (state: any) => {
      state.route = null;
      state.classifyState = "";
      state.classifyError = "";
    },
  },
  extraReducers: (builder: any) =>
    builder
      .addCase(classifyIdentifier.pending, (state: any) => {
        state.classifyState = "pending";
        state.classifyError = "";
      })
      .addCase(classifyIdentifier.fulfilled, (state: any, action: any) => {
        const route = parseRoute(action?.payload);
        state.route = route;
        state.classifyState = "fulfilled";
        state.classifyError = route ? "" : "unrecognized";
      })
      .addCase(classifyIdentifier.rejected, (state: any) => {
        state.classifyState = "failed";
        state.classifyError = "classify_failed";
        state.route = null;
      }),
});

export const { resetClassify } = classifyIdentifierSlice.actions;

export const selectClassifyDetails = (state: any) =>
  state.classifyIdentifier || {};

export default classifyIdentifierSlice.reducer;
